import { AnalyticsBrowser, type Context } from '@segment/analytics-next';

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { fireLsPageView } from '@/utils/google-tag-manager';
import { isSelfHosted } from '@/utils/is-self-hosted';

import { useAuth } from './useAuth/useAuth';
import { useGetLSUserIdFromUser } from './useGetLSUserIdFromUser';
import { useCurrentOrganization, useUser, useWorkspaceId } from './useSwr';

const writeKey =
  import.meta.env.VITE_SEGMENT_WRITE_KEY ?? 'IxOfmiLzN5vT9xJgQEzQvnHioslfvR9S';
const analytics = isSelfHosted ? null : AnalyticsBrowser.load({ writeKey });

export type SegmentInfo = {
  email?: string;
  name?: string;
  organization: { name?: string; id?: string; workspace_id?: string | null };
  user: { supabase_user_id?: string; ls_user_id?: string };
};

const useSegmentInfo = (): {
  data: SegmentInfo;
  isLoading: boolean;
} => {
  const { session } = useAuth();
  const { data: user, isLoading: userLoading } = useUser({
    revalidateOnFocus: false,
  });
  const currentOrg = useCurrentOrganization({
    revalidateOnFocus: false,
  });
  const workspaceId = useWorkspaceId();

  const { email } = session?.user ?? {};
  const { user: userData } = user?.data ?? {};
  const { user_metadata, id: userId } = userData ?? {};
  const { display_name: company, id: orgId, tier } = currentOrg.data ?? {};

  const { lsUserId, isLoading: lsUserIdLoading } =
    useGetLSUserIdFromUser(userId);

  const data = {
    email,
    name: user_metadata?.full_name,
    organization: { name: company, id: orgId, workspace_id: workspaceId, tier },
    user: {
      supabase_user_id: userId,
      ls_user_id: lsUserId,
    },
  };

  return {
    data,
    isLoading: userLoading || lsUserIdLoading || currentOrg.isLoading,
  };
};

/** Should only be called from SegmentAnalyticsProvider to avoid duplicate calls */
export const useSegmentAnalytics = () => {
  const location = useLocation();
  const { data, isLoading: langsmithUserIsLoading } = useSegmentInfo();
  const [identifyPromise, setIdentifyPromise] = useState<
    Promise<Context> | undefined
  >();

  const segmentIdentifyIsLoading = langsmithUserIsLoading || !identifyPromise;

  useEffect(() => {
    if (
      !analytics ||
      langsmithUserIsLoading ||
      isSelfHosted ||
      window.location.hostname === 'localhost'
    )
      return;

    const promise = analytics.identify(data.user.ls_user_id, data);
    setIdentifyPromise(promise);
  }, [data, langsmithUserIsLoading]);

  useEffect(() => {
    if (!analytics || segmentIdentifyIsLoading) return;
    identifyPromise?.then(() => {
      analytics.page();
    });

    // Temporary: Remove when we fully commit to Segment
    fireLsPageView();
  }, [location.pathname, segmentIdentifyIsLoading]);

  return !analytics ? {} : { analytics, data };
};
